import React, { useState, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/hook'
import { RootState } from "../../store/store";
import { fetchVoucherDetails, revokeVoucher, clearRevokeError } from '../../features/voucher/VoucherSlice';
import ERupiVoucherHeader from './ERupiVoucherHeader';
import ERupiVoucherSearch from './ERupiVoucherSearch';
import ERupiVoucherError from './ERupiVoucherError';
import ERupiVoucherDetailsCard from './ERupiVoucherDetailsCard';
import ERupiVoucherTransactionHistory from './ERupiVoucherTransactionHistory';
import RevokeVoucherModal from './RevokeVoucherModal';
import RequestPiiAccessModal from './RequestPiiAccessModal';
import { useMoney } from '../../hooks/useMoney';

const ERupiVoucher = () => {
  const [saralcode, setSaralcode] = useState("")
  const [showRevokeModal, setShowRevokeModal] = useState(false)
  const [showPiiAccessModal, setShowPiiAccessModal] = useState(false)
  const [autoFetchAttempted, setAutoFetchAttempted] = useState(false)
  /** Ephemeral PII for display only - never persisted; cleared on unmount */
  const [displayPii, setDisplayPii] = useState<Record<string, unknown> | null>(null)
  const dispatch = useAppDispatch();
  const { symbol, code } = useMoney();
  const { eRupiVoucher, eRupiVoucherError, eRupiVoucherLoading, revokeLoading, revokeError } = useAppSelector((state: RootState) => state.voucher);

  useEffect(() => {
    return () => {
      setDisplayPii(null);
    };
  }, []);
  
  useEffect(() => {
      window.scrollTo({
        top: 0,
        left: 0
      });
  }, []);

  const onSubmit = () => {
    dispatch(fetchVoucherDetails({
      saralcode: saralcode,
    }))
  }

  // Auto-fetch voucher when navigating from voucher list
  useEffect(() => {
    if (autoFetchAttempted) return; // Only run once
    
    const saralId = localStorage.getItem('erupiVoucherSaralId');
    if (saralId) {
      setSaralcode(saralId);
      dispatch(fetchVoucherDetails({
        saralcode: saralId,
      }));
      // Clear the saralId from localStorage after use
      localStorage.removeItem('erupiVoucherSaralId');
      setAutoFetchAttempted(true);
    } else {
      setAutoFetchAttempted(true);
    }
  }, [dispatch, autoFetchAttempted]);

  const formatAmount = (amount: number | string | null | undefined): string => {
    if (!amount) return '0.00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return numAmount.toFixed(2);
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    // If date is already in DD/MM/YYYY format, return as is
    if (dateString.includes('/') && dateString.split('/').length === 3) {
      return dateString;
    }
    return dateString;
  };

  const handleRevokeVoucher = async () => {
    if (!voucherDetails?.saralCode) {
      console.error('Saral code is missing');
      setShowRevokeModal(false);
      return;
    }

    try {
      const result = await dispatch(revokeVoucher({ saralCode: voucherDetails.saralCode }));
      
      if (revokeVoucher.fulfilled.match(result)) {
        // Refresh the voucher details after successful revocation
        dispatch(fetchVoucherDetails({
          saralcode: voucherDetails.saralCode,
        }));
        setShowRevokeModal(false);
      } else {
        // Error is handled by revokeError state
        console.error('Failed to revoke voucher:', result.payload);
      }
    } catch (error) {
      console.error('Error revoking voucher:', error);
      setShowRevokeModal(false);
    }
  };

  const voucher = eRupiVoucher;
  let voucherDetails: {
    voucherId?: number;
    saralCode: string;
    issueDate: string;
    activateDate: string;
    expiryDate: string;
    revokeDate: string;
    status: string;
    processor: string;
    processorBrand: string;
    bankReferenceCode: string;
    bankReferenceUuid: string;
    ownerMobile: string;
    ownerName: string;
    issueAmount: number;
    balanceAmount: number;
    currency: string;
    programCode: string;
    programName: string;
    transactions: any[];
  } | null = null;

  if (voucher) {
    const statusDisplay = voucher.status
      ? voucher.status.charAt(0).toUpperCase() + voucher.status.slice(1)
      : 'Pending';

    voucherDetails = {
      voucherId: voucher.voucherId,
      saralCode: voucher.saralCode || '-',
      issueDate: voucher.issueDate || '-',
      activateDate: voucher.activateDate || '-',
      expiryDate: voucher.expiryDate || '-',
      revokeDate: voucher.revokeDate || '-',
      status: statusDisplay,
      processor: voucher.processor || '-',
      processorBrand: voucher.processorBrand || '-',
      bankReferenceCode: voucher.bankReferenceCode || '-',
      bankReferenceUuid: voucher.bankReferenceUuid || '-',
      ownerMobile: voucher.ownerMobile || '-',
      ownerName: voucher.ownerName || '-',
      issueAmount: voucher.issueAmount || 0,
      balanceAmount: voucher.balanceAmount || 0,
      currency: voucher.currency || code,
      programCode: voucher.programCode || '-',
      programName: voucher.programName || '-',
      transactions: voucher.transactionHistory || []
    };
  }

  return (
    <div className="bg-white h-full">
      <ERupiVoucherHeader />
      
      <ERupiVoucherSearch
        saralcode={saralcode}
        onSaralcodeChange={setSaralcode}
        onSubmit={onSubmit}
        loading={eRupiVoucherLoading}
      />

      {eRupiVoucherError && <ERupiVoucherError error={eRupiVoucherError} />}

      {/* Show voucher details if available */}
      {voucherDetails && (
        <div className="max-w-7xl mx-auto px-7 py-4">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <ERupiVoucherDetailsCard
              voucherDetails={voucherDetails}
              onRevokeClick={() => {
                dispatch(clearRevokeError());
                setShowRevokeModal(true);
              }}
              onRequestPiiAccess={
                voucherDetails.voucherId != null
                  ? () => setShowPiiAccessModal(true)
                  : undefined
              }
              unmaskedPii={displayPii}
              onClearUnmaskedPii={() => setDisplayPii(null)}
              formatAmount={formatAmount}
              currencySymbol={symbol}
            />

            <ERupiVoucherTransactionHistory
              transactions={voucherDetails.transactions}
              formatDate={formatDate}
              formatAmount={formatAmount}
              currencySymbol={symbol}
            />
          </div>
        </div>
      )}

      <RevokeVoucherModal
        show={showRevokeModal}
        onClose={() => setShowRevokeModal(false)}
        onConfirm={handleRevokeVoucher}
        loading={revokeLoading}
        error={revokeError}
      />

      {voucherDetails?.voucherId != null && (
        <RequestPiiAccessModal
          show={showPiiAccessModal}
          onClose={() => setShowPiiAccessModal(false)}
          entityName="voucher"
          entityId={voucherDetails.voucherId}
          onSuccess={(pii) => setDisplayPii(pii)}
        />
      )}
    </div>
  )
}

export default ERupiVoucher;